import type {
  IOperationActionRequired,
  IOperationResponse,
} from "../services/api";

/** Visual tone for operation status in list + receipt (aligned with send). */
export type OperationStatusTone =
  | "initial"
  | "progress"
  | "completed"
  | "failed"
  | "expired"
  | "action_required";

/** Hex colors for status tones (list text+icon, receipt text). */
export const STATUS_TONE_COLOR: Record<OperationStatusTone, string> = {
  initial: "#F28D19",
  progress: "#252525",
  completed: "#31C859",
  failed: "#FF3B30",
  expired: "#8a8a8a",
  action_required: "#d97706",
};

export const hasOfacDisambiguation = (
  op:
    | Pick<IOperationResponse, "actionRequired">
    | { actionRequired?: IOperationActionRequired | null }
    | null
    | undefined
): boolean => op?.actionRequired?.type === "OFAC_DISAMBIGUATION";

/** i18n key under `receipt.*` for an operation status label. */
export const operationStatusKey = (status: string): string => {
  switch (normalizeOperationStatus(status)) {
    case "completed":
      return "receipt.statusCompleted";
    case "failed":
      return "receipt.statusFailed";
    case "expired":
      return "receipt.statusExpired";
    case "progress":
      return "receipt.statusProcessing";
    case "initial":
    default:
      return "receipt.statusPending";
  }
};

/** Cancelled keeps a distinct label but shares the failed (red) tone. */
export const operationStatusLabelKey = (status: string): string => {
  const s = (status ?? "").toLowerCase();
  if (s === "cancelled" || s === "canceled") {
    return "receipt.statusCancelled";
  }
  return operationStatusKey(status);
};

export const normalizeOperationStatus = (
  status: string | null | undefined
): OperationStatusTone => {
  switch ((status ?? "").toLowerCase()) {
    case "completed":
      return "completed";
    case "failed":
    case "rejected":
    case "cancelled":
    case "canceled":
      return "failed";
    case "expired":
      return "expired";
    case "processing":
      return "progress";
    case "pending":
    case "created":
    default:
      return "initial";
  }
};

export const operationStatusTone = (
  status: string | null | undefined
): OperationStatusTone => normalizeOperationStatus(status);

/** Label key considering soft OFAC actionRequired over coarse status. */
export const operationDisplayStatusKey = (
  op: Pick<IOperationResponse, "status" | "actionRequired">
): string => {
  if (hasOfacDisambiguation(op)) return "receipt.statusActionRequired";
  return operationStatusLabelKey(op.status);
};

/** Tone considering soft OFAC actionRequired over coarse status. */
export const operationDisplayTone = (
  op: Pick<IOperationResponse, "status" | "actionRequired">
): OperationStatusTone => {
  if (hasOfacDisambiguation(op)) return "action_required";
  return operationStatusTone(op.status);
};

export const isCompletedOperationStatus = (status: string): boolean =>
  normalizeOperationStatus(status) === "completed";

export const isTerminalOperationStatus = (status: string): boolean => {
  const tone = normalizeOperationStatus(status);
  return tone === "completed" || tone === "failed" || tone === "expired";
};
